import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
} from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { XsI18nPipe } from '@nx-fe/pipes';
import { ErrorHandlerService, GroupContextService, hd, TicketService } from '@nx-fe/services';
import IGroupModel = hd.IGroupModel;
import { SearchAndPickV2Component } from 'xsshared/components/search-and-pick/search-and-pick.component';
import { createGroupStateObserver } from 'app/utils/group-display.helper';
import { TicketSearchService } from 'app/services/ticket-search.service';
import { SotiPopupTrigger } from '@soti/popup';

@Component({
  selector: 'app-remote-control-embed-incident',
  templateUrl: './remote-control-embed-incident.component.html',
  styleUrls: ['./remote-control-embed-incident.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class RemoteControlEmbedIncidentComponent implements OnInit, OnDestroy {
  @ViewChild('ticketSearch', { static: false }) ticketSelector: SearchAndPickV2Component<hd.ITicketModel>;
  @ViewChild('groupSelector', { static: false }) groupSelector: MatSelect;
  @ViewChild(SotiPopupTrigger, { static: false }) createIncidentPopoutTrigger: SotiPopupTrigger;

  @Output() ticketSelected = new EventEmitter<any>();
  @Output() cancelled = new EventEmitter<boolean>();
  @Input() embeddingDisabled = false;

  private ngUnsubscribe = new Subject<boolean>();
  public createNew = true;
  public groups: IGroupModel[];
  public selectedGroup: IGroupModel;
  public singleGroup = true;
  public opened = false;
  public menuToggle = false;
  public keepOpen = true;
  public expandedMenu: boolean;
  private selectedIncident = undefined;
  ticketsResource: any;

  constructor(
    private groupContext: GroupContextService,
    private ticketService: TicketService,
    private ticketSearchService: TicketSearchService,
    private errorHandlerService: ErrorHandlerService,
    private saI18nPipe: XsI18nPipe,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Setup the ticket resource for searching tickets
    this.ticketsResource = this.ticketSearchService.getTicketsSearchResource(null);
    Object.assign(this.ticketsResource, {
      query: (
        searchParams: { searchText: string; excludeIds: string; excludePublicIds?: string },
        success: Function,
        error: Function
      ) => {
        const searchResults = this.ticketSearchService.searchTickets({
          searchText: searchParams.searchText,
        });
        searchResults.subscribe(
          (data: hd.ISearchResult<hd.IExtendedTicketModel>) => {
            success(data);
          },
          err => {
            error(err);
          }
        );
      },
    });

    this.setupGroups();
    this.groupContext.getGroups();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(true);
    this.ngUnsubscribe.complete();
  }

  /** Handle the visibility change of the expanded menu */
  public setExpandedMenu = (menuEvent: any) => {
    setTimeout(() => {
      this.expandedMenu = menuEvent.isVisible;
      if (!this.expandedMenu) {
        if (this.groupSelector) {
          this.groupSelector.close();
        }
      } else {
        this.errorHandlerService.clearError();
        this.groupContext.refreshGroups();
      }
    });
  };

  /** Toggle the menu visibility */
  public toggleMenu = () => {
    if (this.menuToggle) {
      this.keepOpen = false;
    }
    setTimeout(() => {
      this.menuToggle = !this.menuToggle;
    });
  };

  /** Setup the groups based on context service */
  setupGroups = () => {
    const groups = this.groupContext.allowedGroupsObservable;
    const ticketConfig = this.ticketService.getTicketUserConfiguration();

    createGroupStateObserver(groups, ticketConfig)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(result => {
        if (result.groups.length < 1) {
          this.createNew = false;
          this.cdRef.detectChanges();
        }
        // Use type assertion to ensure type compatibility
        this.groups = result.groups as unknown as IGroupModel[];
        this.selectedGroup = result.lastSelectedGroup as unknown as IGroupModel;
        this.singleGroup = result.isSingleGroup;
      });
  };

  /** Start creating an incident */
  startCreateIncident = () => {
    if (this.embeddingDisabled) {
      return;
    }

    if (this.groups && this.groups.length === 0) {
      this.createNew = false;
    }
    this.keepOpen = true;
    this.toggleMenu();
  };

  /** Filter tickets based on status */
  filterTickets = (tickets: hd.ITicketModel[]): hd.ITicketModel[] => {
    return tickets.filter(ticket => ticket.status !== 'draft');
  };

  /** Cancel the current action and close the menu */
  cancel = () => {
    this.keepOpen = false;
    this.toggleMenu();
    this.cancelled.emit(true);
  };

  /** Select an incident from the search results */
  public selectIncident = (selectedIncident: any) => {
    this.selectedIncident = selectedIncident;
  };

  /** Handle the ticket selection process */
  public selectTicket = () => {
    if (this.createNew) {
      if (this.groups && this.groups.length === 0) {
        const error: hd.IError = {
          error_description: this.saI18nPipe.transform('error_NoGroupsCreateTicket'),
        };
        this.errorHandlerService.setError(error);
      } else if (this.selectedGroup && this.selectedGroup.groupId > 0) {
        this.ticketSelected.emit({ groupId: this.selectedGroup.groupId });
      } else {
        const error: hd.IError = {
          error_description: this.saI18nPipe.transform('error_NoExistingTicket'),
        };
        this.errorHandlerService.setError(error);
      }
    } else {
      if (this.selectedIncident) {
        this.ticketSelected.emit(this.selectedIncident);
      } else {
        const error: hd.IError = {
          error_description: this.saI18nPipe.transform('error_NoExistingTicket'),
        };
        this.errorHandlerService.setError(error);
      }
    }
    if (!this.keepOpen) {
      this.toggleMenu();
    }
  };

  /** Check if saving is disabled */
  isSaveDisabled = (): boolean => {
    if (!this.createNew && this.ticketSelector && !this.ticketSelector.selectedItem) {
      return true;
    }
    return false;
  };

  /** Handle click on mat-option and hide the tooltip */
  onOptionClick(tooltip: any) {
    if (tooltip && tooltip.hide) {
      tooltip.hide(); // Assuming soti-tooltip has a hide method
    }
  }
}
